import { z } from "zod";

// UK postcode regex pattern
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

export const jobPostingSchema = z.object({
  jobTitle: z
    .string()
    .trim()
    .min(1, "Job title is required")
    .max(200, "Job title must be less than 200 characters"),
  
  companyName: z
    .string()
    .trim()
    .min(1, "Company name is required")
    .max(100, "Company name must be less than 100 characters"),
  
  location: z
    .string()
    .trim()
    .min(1, "Location is required")
    .regex(UK_POSTCODE_REGEX, "Please enter a valid UK postcode (e.g., E1 6AN)"),
  
  employmentType: z
    .string()
    .min(1, "Employment type is required"),
  
  salaryMin: z
    .string()
    .refine(
      (val) => {
        const num = parseInt(val);
        return !isNaN(num) && num >= 10 && num <= 1000000;
      },
      "Minimum salary must be between £10 and £1,000,000"
    ),
  
  salaryMax: z
    .string()
    .refine(
      (val) => {
        const num = parseInt(val);
        return !isNaN(num) && num >= 10 && num <= 1000000;
      },
      "Maximum salary must be between £10 and £1,000,000"
    ),
  
  description: z
    .string()
    .trim()
    .min(50, "Job description must be at least 50 characters")
    .max(5000, "Job description must be less than 5000 characters"),
  
  skills: z
    .array(
      z.string().trim().max(50, "Each skill must be less than 50 characters")
    )
    .max(20, "You can add up to 20 skills"),
  
  openToCandidates: z.boolean(),
}).refine(
  (data) => {
    const minSalary = parseInt(data.salaryMin);
    const maxSalary = parseInt(data.salaryMax);
    return maxSalary >= minSalary;
  },
  {
    message: "Maximum salary must be greater than or equal to minimum salary",
    path: ["salaryMax"],
  }
);

export type JobPostingFormData = z.infer<typeof jobPostingSchema>;
